import { useState, useEffect } from "react";
import axios from "axios";
import { API_URI } from "../../../constants/GlobalConstants";
import { useDispatch, useSelector } from "react-redux";
import { 
  Card, CardContent, Typography, IconButton, Box, CircularProgress, Alert, 
  Select, MenuItem, FormControl, InputLabel, Grid, Paper,
  useTheme, useMediaQuery, Container
} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend, LineElement, PointElement } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { ListDealerAction } from "../../../redux/actions/DealerAction";
import moment from "moment-timezone";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

const MonthlySales = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [totalSales, setTotalSales] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dailySales, setDailySales] = useState([]);
  const [salesByHour, setSalesByHour] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState("");
  const [salesGrowth, setSalesGrowth] = useState(0);
  const [timezone, setTimezone] = useState("UTC");

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const listDealer = useSelector((state) => state.listDealer);
  const dispatch = useDispatch();
  const { loading: loadingClient, dealer, success } = listDealer;

  const fetchSales = async () => {
    if (!userInfo?.token) return;
    
    setLoading(true);
    setError("");

    try {
      const { data } = await axios.get(
        `${API_URI}/api/analytics/monthly-sales?year=${year}&month=${month}&dealerId=${selectedDealer}`,
        {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        }
      );
      setTotalSales(data.totalSales);
      setDailySales(data.dailySales || []);
      setSalesByHour(data.salesByHour || []);
      setTimezone(data.timezone || "UTC");
      setSalesGrowth(parseFloat(data.salesGrowth)); 
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch sales data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
    dispatch(ListDealerAction(userInfo.token));
  }, [month, year, selectedDealer, userInfo]);
  
  const handlePrevMonth = () => {
    setMonth((prev) => (prev === 1 ? 12 : prev - 1));
    if (month === 1) setYear((prev) => prev - 1);
  };

  const handleNextMonth = () => {
    setMonth((prev) => (prev === 12 ? 1 : prev + 1));
    if (month === 12) setYear((prev) => prev + 1);
  };

  const getMonthName = (monthNum) => {
    return new Date(2024, monthNum - 1).toLocaleString('default', { month: 'long' });
  };

  const dailySalesChartData = {
    labels: dailySales.length > 0 ? dailySales.map(sale => sale.date) : ["No Data"],
    datasets: [
      {
        label: 'Daily Sales',
        data: dailySales.length > 0 ? dailySales.map(sale => sale.amount) : [0],
        backgroundColor: theme.palette.primary.main,
        borderRadius: 4,
        barThickness: 20,
      }
    ]
  };

  const salesByHourChartData = {
    labels: salesByHour.length > 0 ? salesByHour.map(sale => `${sale._id}:00`) : ["No Data"],
    datasets: [
      {
        label: 'Sales by Hour',
        data: salesByHour.length > 0 ? salesByHour.map(sale => sale.totalSales) : [0],
        borderColor: theme.palette.secondary.main,
        backgroundColor: `${theme.palette.secondary.main}33`,
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.3,
        fill: true,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `$${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        }
      },
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: isMobile ? 45 : 0,
          autoSkip: true,
          maxTicksLimit: isMobile ? 7 : 15,
        }
      }
    }
  };

  return (
    <Container maxWidth="xl" className="py-4">
      <Card elevation={3} className="w-100 shadow-lg">
        <CardContent className="p-0">
          <Box className="p-4 bg-primary text-white" sx={{ backgroundColor: theme.palette.primary.main }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item xs={12} md={6}>
                <Typography variant="h5" className="font-weight-bold mb-1">
                  Monthly Sales Dashboard
                </Typography>
                <Typography variant="body2" className="opacity-75">
                  Track your sales performance ({timezone})
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} className="d-flex justify-content-md-end align-items-center mt-3 mt-md-0">
                <FormControl variant="outlined" size="small" className="bg-white rounded me-3" sx={{ minWidth: 200 }}>
                  <InputLabel className="text-dark">Select Dealer</InputLabel>
                  <Select
                    value={selectedDealer}
                    onChange={(e) => setSelectedDealer(e.target.value)}
                    label="Select Dealer"
                  >
                    <MenuItem value="">All Dealers</MenuItem>
                    {dealer?.map((dealer) => (
                      <MenuItem key={dealer._id} value={dealer._id}>
                        {dealer.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box className="d-flex align-items-center bg-white bg-opacity-25 rounded p-2">
                  <IconButton onClick={handlePrevMonth} size="small" className="text-white">
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="subtitle1" className="mx-2 text-white">
                    {getMonthName(month)} {year}
                  </Typography>
                  <IconButton onClick={handleNextMonth} size="small" className="text-white">
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {loading ? (
            <Box className="d-flex justify-content-center p-5">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error Distance between two points formula" className="m-4">
              {error}
            </Alert>
          ) : (
            <Box className="p-4">
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={2} className="p-4 h-100">
                    <Box className="d-flex align-items-center mb-3">
                      <AttachMoneyIcon fontSize="large" className="me-2" color="primary" />
                      <Typography variant="h6" className="font-weight-bold">
                        Total Sales
                      </Typography>
                    </Box>
                    <Typography variant="h3" className="font-weight-bold mb-2 text-primary" color="primary">
                      ${totalSales.toLocaleString()}
                    </Typography>
                    <Box className="d-flex align-items-center mt-2">
                      <TrendingUpIcon 
                        fontSize="small" 
                        className="me-1" 
                        color={salesGrowth >= 0 ? "success" : "error"} 
                      />
                      <Typography 
                        variant="body2" 
                        color={salesGrowth >= 0 ? "success" : "error"}
                      >
                        {salesGrowth}% from previous month
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Paper elevation={2} className="p-4 h-100">
                    <Typography variant="h6" className="font-weight-bold mb-3">
                      Sales by Hour
                    </Typography>
                    <Box style={{ height: "200px" }}>
                      <Line data={salesByHourChartData} options={chartOptions} />
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper elevation={2} className="p-4">
                    <Typography variant="h6" className="font-weight-bold mb-3">
                      Daily Sales
                    </Typography>
                    <Box style={{ height: "300px" }}>
                      <Bar data={dailySalesChartData} options={chartOptions} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default MonthlySales;